import {computed, ref} from 'vue';
import {defineStore} from 'pinia';
import {FetchError, SubmissionState, type AdvancedSearchResponse, type UpdateAdvancedSearchByIdRequest, type SelectedProfile} from '@api';
import {useApis} from '~/composables';
import {useGenericErrorHandler} from '~/composables/useGenericErrorHandler';

export type ProjectDescriptionGenerationOutcome = 'GENERATED' | 'RATE_LIMITED' | 'FAILED';

export const useAdvancedSearchStore = defineStore('advanced-search', () => {
    const {advancedSearchApi, advancedSearchAuthenticatedApi} = useApis();
    const {handleError} = useGenericErrorHandler();
    const advancedSearch = ref<AdvancedSearchResponse | undefined>();

    const loading = ref<boolean>(false);

    const hasFreelancersSelection = computed((): boolean => !!advancedSearch.value?.selection?.length);
    const isProvidedOnlySelection = computed((): boolean => advancedSearch.value?.project?.expectedSourcingProcess === 'PROVIDED_ONLY_SELECTION');
    const isDelegated = computed((): boolean => advancedSearch.value?.project?.expectedSourcingProcess === 'DELEGATED');
    const isWithAssistance = computed((): boolean => advancedSearch.value?.project?.expectedSourcingProcess === 'WITH_ASSISTANCE');
    const isDelegatedWithSelection = computed((): boolean => isDelegated.value && !hasFreelancersSelection.value);

    async function loadAdvancedSearch(briefId: string) {
        try {
            loading.value = true;
            advancedSearch.value = await advancedSearchApi.findAdvancedSearchByBriefId({briefId});
        } catch (error: any) {
            if (error instanceof FetchError && (error.statusCode === 404 || error.statusCode === 403)) {
                throw error;
            }
            handleError(error as FetchError);
        } finally {
            loading.value = false;
        }
    }

    async function updateAdvancedSearch(requestParameters: UpdateAdvancedSearchByIdRequest): Promise<boolean> {
        if (advancedSearch.value?.submissionState === SubmissionState.Submitted) {
            return true;
        }

        try {
            loading.value = true;
            advancedSearch.value = await advancedSearchApi.updateAdvancedSearchById(requestParameters);
            return true;
        } catch (error) {
            handleError(error as FetchError);
            return false;
        } finally {
            loading.value = false;
        }
    }

    async function generateProjectDescription(advancedSearchId: string): Promise<ProjectDescriptionGenerationOutcome> {
        try {
            loading.value = true;
            advancedSearch.value = await advancedSearchApi.generateAdvancedSearchProjectDescription({advancedSearchId});
            return 'GENERATED';
        } catch (error) {
            return error instanceof FetchError && error.statusCode === 429 ? 'RATE_LIMITED' : 'FAILED';
        } finally {
            loading.value = false;
        }
    }

    async function submitAdvancedSearch(redirect?: RequestRedirect) {
        if (!advancedSearch.value) {
            throw new Error('Unable to submit advanced search before initializing the store');
        }

        const {selection} = advancedSearch.value;

        const submittedAdvancedSearch = await advancedSearchAuthenticatedApi.submitAdvancedSearchByBriefId(
            {
                briefId: advancedSearch.value.briefId,
            },
            {redirect},
        );

        // Once the search is submitted, the API cleans the selection but we still need it on frontend side
        advancedSearch.value = {...submittedAdvancedSearch, selection};
    }

    async function clearSelection(advancedSearchId: string, advancedSearchQueryId?: string) {
        try {
            loading.value = true;
            advancedSearch.value = await advancedSearchApi.clearSelection({
                advancedSearchId,
                clearFreelancerSelectionRequest: {
                    advancedSearchQueryId,
                },
            });
        } catch (error) {
            handleError(error as FetchError);
        } finally {
            loading.value = false;
        }
    }

    async function selectFreelancers(advancedSearchId: string, advancedSearchQueryId: string, profiles: SelectedProfile[]) {
        try {
            loading.value = true;
            advancedSearch.value = await advancedSearchApi.addFreelancersToSelection({
                advancedSearchId,
                selectFreelancersRequest: {
                    advancedSearchQueryId,
                    profiles,
                },
            });
        } catch (error) {
            handleError(error as FetchError);
        } finally {
            loading.value = false;
        }
    }

    async function unselectFreelancer(advancedSearchId: string, advancedSearchQueryId: string, profileId: string, position?: number) {
        try {
            loading.value = true;
            advancedSearch.value = await advancedSearchApi.removeFreelancersFromSelection({
                advancedSearchId,
                unselectFreelancersRequest: {
                    advancedSearchQueryId,
                    profiles: [{profileId, position}],
                },
            });
        } catch (error) {
            handleError(error as FetchError);
        } finally {
            loading.value = false;
        }
    }

    async function rejectFreelancer(advancedSearchId: string, advancedSearchQueryId: string, profileId: string, position: number) {
        await advancedSearchApi.rejectFreelancers({
            advancedSearchId,
            rejectFreelancersRequest: {
                advancedSearchQueryId,
                profiles: [{profileId, position}],
            },
        });
    }

    return {
        loading,
        advancedSearch,
        loadAdvancedSearch,
        updateAdvancedSearch,
        generateProjectDescription,
        submitAdvancedSearch,
        clearSelection,
        selectFreelancers,
        unselectFreelancer,
        isDelegated,
        isProvidedOnlySelection,
        isDelegatedWithSelection,
        rejectFreelancer,
        isWithAssistance,
        hasFreelancersSelection,
    };
});
